import type { DateRange } from "@/types";
import {
  getUnixTime,
  format,
  startOfHour,
  addHours,
  subYears,
  addYears,
  startOfDay,
} from "date-fns";
import { da } from "date-fns/locale";

export const convertToDate = (value: number | Date) => {
  return typeof value === "number" ? toDate(value) : value;
};

/**
 * Convert unix timestamp to Date object
 *
 * @param unix number
 * @returns Date
 * @example
 *  <script setup lang="ts">
 *    const date: Date = toDate(1697700327)
 *  </script>
 */
export const toDate = (unix: number) => {
  return new Date(unix * 1000);
};

/**
 * Convert Date object to unix timestamp
 *
 * @param unix number
 * @returns Date
 * @example
 *  <script setup lang="ts">
 *    const value: number = toUnix(new Date())
 *  </script>
 */
export const toUnix = (date: Date) => {
  return Math.round(getUnixTime(date));
};

export const toDayName = (date: number) => {
  const name = format(toDate(date), "iiii", {
    locale: da,
  });
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const toWeekNumber = (date: number) => {
  return format(toDate(date), "ww");
};

export const fromUnixToAPIDate = (date: number) => {
  return toAPIDate(toDate(date));
};

export const toAPIDate = (date: Date) => {
  return date.toISOString().split(".")[0] + "Z";
};

export const getStartOfToday = () => {
  return toUnix(startOfDay(new Date()));
};

export const getStartDate = () => {
  // It would return 14:00 if the time is 13:45
  const now = new Date();
  return toUnix(addHours(startOfHour(now), 1));
};

export const getEndDate = () => {
  // It would return 15:00 if the time is 13:45
  const now = new Date();
  return toUnix(addHours(startOfHour(now), 2));
};

export const toLongDateRange = (future: boolean): DateRange => {
  let dateFrom = subYears(new Date(), 100);
  let dateTo = new Date();

  if (future) {
    dateFrom = new Date();
    dateTo = addYears(new Date(), 100);
  }

  return { dateFrom: toAPIDate(dateFrom), dateTo: toAPIDate(dateTo) };
};

export const toExternalDateRange = (): DateRange => {
  const dateFrom = startOfDay(new Date());
  const dateTo = addYears(new Date(), 100);

  return { dateFrom: toAPIDate(dateFrom), dateTo: toAPIDate(dateTo) };
};

export const normalizeTime = (days: number, hours: number, minutes: number) => {
  // Convert minutes to hours
  hours += Math.floor(minutes / 60);
  minutes = minutes % 60;

  // Convert hours to days
  days += Math.floor(hours / 24);
  hours = hours % 24;

  return { days, hours, minutes };
};

export const minutesToTime = (totalMinutes: number) => {
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  return { days, hours, minutes };
};
